import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import BackButton from '../../components/BackButton';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Search, Building, User as UserIcon, Landmark } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const emptyForm = {
  nombre_fiscal: '', nombre_comercial: '', cif_nif: '',
  tipo_persona: 'juridica',
  direccion: '', codigo_postal: '', poblacion: '', provincia: '',
  pais: 'España', codigo_pais: 'ES',
  email: '', telefono: '', persona_contacto: '', email_facturacion: '',
  is_public_entity: false,
  dir3_oficina_contable: '', dir3_organo_gestor: '', dir3_unidad_tramitadora: '',
  dir3_organo_proponente: '', codigo_expediente: '',
  iban: '', forma_pago: 'Transferencia', dias_vencimiento: 30,
  aplicar_retencion: false, porcentaje_retencion: 0,
  iva_por_defecto: '', exento_iva: false, motivo_exencion: '',
  notas: '', activo: true
};

const Clients = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const canEdit = user?.role === 'Administrador' || user?.role === 'Formador';

  useEffect(() => { fetchClients(); }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/billing/clients`);
      setClients(res.data);
    } catch (e) {
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowDialog(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...emptyForm, ...c, iva_por_defecto: c.iva_por_defecto ?? '' });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      payload.dias_vencimiento = parseInt(payload.dias_vencimiento || 30);
      payload.porcentaje_retencion = parseFloat(payload.porcentaje_retencion || 0);
      payload.iva_por_defecto = payload.iva_por_defecto === '' ? null : parseFloat(payload.iva_por_defecto);

      if (editing) {
        await axios.put(`${API_URL}/billing/clients/${editing.id}`, payload);
        toast.success('Cliente actualizado');
      } else {
        await axios.post(`${API_URL}/billing/clients`, payload);
        toast.success('Cliente creado');
      }
      setShowDialog(false);
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este cliente? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`${API_URL}/billing/clients/${id}`);
      toast.success('Cliente eliminado');
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const filtered = clients.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      c.nombre_fiscal?.toLowerCase().includes(s) ||
      c.nombre_comercial?.toLowerCase().includes(s) ||
      c.cif_nif?.toLowerCase().includes(s) ||
      c.email?.toLowerCase().includes(s)
    );
  });

  return (
    <Layout>
      <div className="space-y-6" data-testid="clients-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <BackButton to="/billing" label="Volver a Intranet" />
            <h1 className="text-3xl font-manrope font-bold text-slate-900">Clientes</h1>
            <p className="text-slate-500 mt-1">Gestión de clientes (empresas, particulares y entidades públicas)</p>
          </div>
          {canEdit && (
            <Button data-testid="new-client-button" onClick={openCreate} className="bg-[#004E89] hover:bg-[#003a68]">
              <Plus className="w-4 h-4 mr-2" /> Nuevo cliente
            </Button>
          )}
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input data-testid="clients-search" className="pl-9" placeholder="Buscar por nombre, CIF, email..." value={search} onChange={e=>setSearch(e.target.value)} />
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          {loading ? (
            <div className="p-12 text-center text-slate-500">Cargando...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              {search ? 'No se encontraron clientes.' : 'Todavía no tienes clientes registrados.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Tipo</th>
                    <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Nombre fiscal</th>
                    <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">CIF/NIF</th>
                    <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Población</th>
                    <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Email</th>
                    <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Estado</th>
                    <th className="text-right p-3 text-xs font-semibold text-slate-700 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(c => (
                    <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`client-row-${c.id}`}>
                      <td className="p-3">
                        {c.is_public_entity ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                            <Landmark className="w-3 h-3" /> Pública
                          </span>
                        ) : c.tipo_persona === 'fisica' ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700">
                            <UserIcon className="w-3 h-3" /> Física
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700">
                            <Building className="w-3 h-3" /> Jurídica
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-sm font-medium text-slate-900">
                        {c.nombre_fiscal}
                        {c.nombre_comercial && <div className="text-xs text-slate-500">{c.nombre_comercial}</div>}
                      </td>
                      <td className="p-3 text-sm text-slate-700 font-mono">{c.cif_nif}</td>
                      <td className="p-3 text-sm text-slate-600">{c.poblacion}{c.provincia ? `, ${c.provincia}` : ''}</td>
                      <td className="p-3 text-sm text-slate-600">{c.email || '-'}</td>
                      <td className="p-3">
                        <span className={`inline-block text-xs px-2 py-1 rounded-full ${c.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {c.activo ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="p-3 text-right space-x-2">
                        {canEdit && (
                          <>
                            <button data-testid={`edit-client-${c.id}`} onClick={()=>openEdit(c)} className="text-slate-600 hover:text-blue-600">
                              <Edit className="w-4 h-4 inline" />
                            </button>
                            <button data-testid={`delete-client-${c.id}`} onClick={()=>handleDelete(c.id)} className="text-slate-600 hover:text-red-600">
                              <Trash2 className="w-4 h-4 inline" />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? 'Editar cliente' : 'Nuevo cliente'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Fiscales */}
              <fieldset className="border border-slate-200 rounded-lg p-4">
                <legend className="text-sm font-semibold text-slate-700 px-2">Datos fiscales</legend>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    <Label>Nombre fiscal *</Label>
                    <Input data-testid="client-form-nombre" required value={form.nombre_fiscal} onChange={e=>set('nombre_fiscal', e.target.value)} />
                  </div>
                  <div>
                    <Label>Nombre comercial</Label>
                    <Input value={form.nombre_comercial} onChange={e=>set('nombre_comercial', e.target.value)} />
                  </div>
                  <div>
                    <Label>CIF/NIF *</Label>
                    <Input data-testid="client-form-cif" required value={form.cif_nif} onChange={e=>set('cif_nif', e.target.value)} />
                  </div>
                  <div>
                    <Label>Tipo de persona</Label>
                    <select className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm" value={form.tipo_persona} onChange={e=>set('tipo_persona', e.target.value)}>
                      <option value="juridica">Jurídica (Empresa)</option>
                      <option value="fisica">Física (Autónomo/Particular)</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2 mt-6">
                    <input type="checkbox" id="is_public_entity" checked={form.is_public_entity} onChange={e=>set('is_public_entity', e.target.checked)} />
                    <label htmlFor="is_public_entity" className="text-sm">Entidad pública (FACe)</label>
                  </div>
                </div>
              </fieldset>

              {/* Dirección */}
              <fieldset className="border border-slate-200 rounded-lg p-4">
                <legend className="text-sm font-semibold text-slate-700 px-2">Dirección</legend>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    <Label>Dirección</Label>
                    <Input value={form.direccion} onChange={e=>set('direccion', e.target.value)} />
                  </div>
                  <div>
                    <Label>Código postal</Label>
                    <Input value={form.codigo_postal} onChange={e=>set('codigo_postal', e.target.value)} />
                  </div>
                  <div>
                    <Label>Población</Label>
                    <Input value={form.poblacion} onChange={e=>set('poblacion', e.target.value)} />
                  </div>
                  <div>
                    <Label>Provincia</Label>
                    <Input value={form.provincia} onChange={e=>set('provincia', e.target.value)} />
                  </div>
                  <div>
                    <Label>País</Label>
                    <Input value={form.pais} onChange={e=>set('pais', e.target.value)} />
                  </div>
                </div>
              </fieldset>

              {/* Contacto */}
              <fieldset className="border border-slate-200 rounded-lg p-4">
                <legend className="text-sm font-semibold text-slate-700 px-2">Contacto</legend>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Email</Label>
                    <Input type="email" value={form.email} onChange={e=>set('email', e.target.value)} />
                  </div>
                  <div>
                    <Label>Email facturación</Label>
                    <Input type="email" value={form.email_facturacion} onChange={e=>set('email_facturacion', e.target.value)} placeholder="Si distinto del principal" />
                  </div>
                  <div>
                    <Label>Teléfono</Label>
                    <Input value={form.telefono} onChange={e=>set('telefono', e.target.value)} />
                  </div>
                  <div>
                    <Label>Persona de contacto</Label>
                    <Input value={form.persona_contacto} onChange={e=>set('persona_contacto', e.target.value)} />
                  </div>
                </div>
              </fieldset>

              {/* FACe / DIR3 */}
              {form.is_public_entity && (
                <fieldset className="border border-blue-200 bg-blue-50/40 rounded-lg p-4">
                  <legend className="text-sm font-semibold text-blue-800 px-2">Códigos DIR3 (FACe)</legend>
                  <p className="text-xs text-slate-600 mb-3">Requeridos para facturar a Administraciones Públicas. Consulta el <a href="https://face.gob.es/es/directorio-de-organismos" target="_blank" rel="noreferrer" className="text-blue-700 underline">directorio DIR3</a>.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label>Oficina contable *</Label>
                      <Input value={form.dir3_oficina_contable} onChange={e=>set('dir3_oficina_contable', e.target.value)} placeholder="p.ej. GE0001234" />
                    </div>
                    <div>
                      <Label>Órgano gestor *</Label>
                      <Input value={form.dir3_organo_gestor} onChange={e=>set('dir3_organo_gestor', e.target.value)} />
                    </div>
                    <div>
                      <Label>Unidad tramitadora *</Label>
                      <Input value={form.dir3_unidad_tramitadora} onChange={e=>set('dir3_unidad_tramitadora', e.target.value)} />
                    </div>
                    <div>
                      <Label>Órgano proponente</Label>
                      <Input value={form.dir3_organo_proponente} onChange={e=>set('dir3_organo_proponente', e.target.value)} />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Nº de expediente</Label>
                      <Input value={form.codigo_expediente} onChange={e=>set('codigo_expediente', e.target.value)} />
                    </div>
                  </div>
                </fieldset>
              )}

              {/* Facturación */}
              <fieldset className="border border-slate-200 rounded-lg p-4">
                <legend className="text-sm font-semibold text-slate-700 px-2">Condiciones de facturación</legend>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>IBAN</Label>
                    <Input value={form.iban} onChange={e=>set('iban', e.target.value)} />
                  </div>
                  <div>
                    <Label>Forma de pago</Label>
                    <select className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm" value={form.forma_pago} onChange={e=>set('forma_pago', e.target.value)}>
                      <option>Transferencia</option>
                      <option>Domiciliación</option>
                      <option>Efectivo</option>
                      <option>Tarjeta</option>
                      <option>Otro</option>
                    </select>
                  </div>
                  <div>
                    <Label>Días de vencimiento</Label>
                    <Input type="number" value={form.dias_vencimiento} onChange={e=>set('dias_vencimiento', e.target.value)} />
                  </div>
                  <div>
                    <Label>IVA por defecto (%) — opcional</Label>
                    <Input type="number" step="0.01" value={form.iva_por_defecto} onChange={e=>set('iva_por_defecto', e.target.value)} placeholder="Deja vacío para usar el global" />
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="exento_iva" checked={form.exento_iva} onChange={e=>set('exento_iva', e.target.checked)} />
                    <label htmlFor="exento_iva" className="text-sm">Exento de IVA (servicios sanitarios, etc.)</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="aplicar_retencion" checked={form.aplicar_retencion} onChange={e=>set('aplicar_retencion', e.target.checked)} />
                    <label htmlFor="aplicar_retencion" className="text-sm">Aplicar retención IRPF</label>
                  </div>
                  {form.aplicar_retencion && (
                    <div>
                      <Label>% Retención</Label>
                      <Input type="number" step="0.01" value={form.porcentaje_retencion} onChange={e=>set('porcentaje_retencion', e.target.value)} />
                    </div>
                  )}
                  {form.exento_iva && (
                    <div className="md:col-span-2">
                      <Label>Motivo exención</Label>
                      <Input value={form.motivo_exencion} onChange={e=>set('motivo_exencion', e.target.value)} placeholder="p.ej. Servicios sanitarios exentos (art. 20 LIVA)" />
                    </div>
                  )}
                </div>
              </fieldset>

              <div>
                <Label>Notas internas</Label>
                <textarea className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm" rows={2} value={form.notas} onChange={e=>set('notas', e.target.value)} />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={()=>setShowDialog(false)}>Cancelar</Button>
                <Button type="submit" data-testid="client-form-submit" className="bg-[#004E89] hover:bg-[#003a68]">
                  {editing ? 'Guardar cambios' : 'Crear cliente'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Clients;
